import { useMemo } from 'react';
import type { EngagementFieldAvailability, EngagementHeatmapCellDTO } from '@/api/generated/model';
import { formatHeatmapSlotRange, HeatmapGridCell } from '@/components/HeatmapGridCell';
import { TimezoneCountryStateFilter } from '@/components/TimezoneCountryStateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HEATMAP_TIMEZONES } from '@/lib/heatmapTimezones';
import { ENGAGEMENT_LOCATION_HELP } from '../lib/engagementColors';
import { SectionLocationHint } from './EngagementChartFilters';
import { EngagementBusy, NotReadyBadge, NotReadyNote, engagementReadyState } from './EngagementNotReady';

const DAYS = [
  { short: 'Mon', full: 'Monday' },
  { short: 'Tue', full: 'Tuesday' },
  { short: 'Wed', full: 'Wednesday' },
  { short: 'Thu', full: 'Thursday' },
  { short: 'Fri', full: 'Friday' },
  { short: 'Sat', full: 'Saturday' },
  { short: 'Sun', full: 'Sunday' },
] as const;

const TIME_SLOTS = [
  { label: '12 AM', startHour: 0 },
  { label: '2 AM', startHour: 2 },
  { label: '4 AM', startHour: 4 },
  { label: '6 AM', startHour: 6 },
  { label: '8 AM', startHour: 8 },
  { label: '10 AM', startHour: 10 },
  { label: '12 PM', startHour: 12 },
  { label: '2 PM', startHour: 14 },
  { label: '4 PM', startHour: 16 },
  { label: '6 PM', startHour: 18 },
  { label: '8 PM', startHour: 20 },
  { label: '10 PM', startHour: 22 },
] as const;

const HEAT_COLORS = ['#1c2438', '#2b4a72', '#3d7cb5', '#6fb2e0', '#d8ecfb'] as const;

export const ENGAGEMENT_HEATMAP_TIMEZONES = HEATMAP_TIMEZONES;

/** Shared IANA picker: country first, then state / region. */
export function EngagementTimezoneFilter({
  timezone,
  onTimezoneChange,
  ariaLabel = 'Change heatmap timezone',
  className,
}: {
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <TimezoneCountryStateFilter
      timezone={timezone}
      onTimezoneChange={onTimezoneChange}
      ariaLabel={ariaLabel}
      detail="Heatmap timezone"
      {...(className ? { className } : {})}
    />
  );
}

function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return HEAT_COLORS[0];
  const ratio = value / max;
  if (ratio < 0.2) return HEAT_COLORS[0];
  if (ratio < 0.4) return HEAT_COLORS[1];
  if (ratio < 0.6) return HEAT_COLORS[2];
  if (ratio < 0.8) return HEAT_COLORS[3];
  return HEAT_COLORS[4];
}

/**
 * Figma "Engagement by Time of Day": two-hour rows against weekday columns,
 * scaled against the busiest cell in the window.
 */
export function EngagementHeatmapCard({
  availability,
  note,
  timezone,
  onTimezoneChange,
  cells,
  isLoading,
}: {
  availability?: EngagementFieldAvailability;
  note?: string;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  cells?: EngagementHeatmapCellDTO[];
  isLoading?: boolean;
}) {
  const state = engagementReadyState(availability);
  const { grid, max } = useMemo(() => {
    const next = Array.from({ length: TIME_SLOTS.length }, () =>
      Array.from({ length: 7 }, () => 0),
    );
    let peak = 0;
    for (const cell of cells ?? []) {
      const dow = cell.dayOfWeek;
      const hour = cell.hour;
      const count = Number(cell.count) || 0;
      if (dow == null || hour == null || dow < 1 || dow > 7 || hour < 0 || hour > 23) continue;
      const slot = Math.floor(hour / 2);
      const day = dow - 1;
      next[slot]![day]! += count;
      if (next[slot]![day]! > peak) peak = next[slot]![day]!;
    }
    return { grid: next, max: peak };
  }, [cells]);

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="p-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">Engagement by Time of Day</CardTitle>
              <SectionLocationHint
                text={ENGAGEMENT_LOCATION_HELP.audience}
                label={`How Engagement by Time of Day uses the location filter. ${ENGAGEMENT_LOCATION_HELP.audience}`}
              />
            </div>
            <p className="text-[11px] font-medium text-muted-foreground">
              User-engagement activity across days of the week and times of day.
            </p>
          </div>
          <NotReadyBadge state={state} hint={note} />
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3 pt-3">
          <EngagementTimezoneFilter timezone={timezone} onTimezoneChange={onTimezoneChange} />
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            Low
            <span className="flex gap-1">
              {HEAT_COLORS.map((color) => (
                <span
                  key={color}
                  className="h-3 w-5 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
            </span>
            High
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-0">
        {isLoading ? (
          <EngagementBusy label="Loading heatmap…" />
        ) : state === 'not-ready' ? (
          <NotReadyNote>{note ?? 'Heatmap unavailable until PostHog is configured.'}</NotReadyNote>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid min-w-[560px] grid-cols-[52px_repeat(7,minmax(0,1fr))] gap-1">
              {TIME_SLOTS.map((slot, slotIndex) => (
                <div key={slot.label} className="contents">
                  <div className="flex items-center text-[10px] text-muted-foreground">
                    {slot.label}
                  </div>
                  {DAYS.map((day, dayIndex) => {
                    const value = grid[slotIndex]![dayIndex]!;
                    const shareOfPeak =
                      max > 0 && value > 0
                        ? `${Math.round((value / max) * 100)}% of peak`
                        : undefined;
                    return (
                      <HeatmapGridCell
                        key={`${slot.label}-${day.short}`}
                        dayLabel={day.full}
                        timeLabel={formatHeatmapSlotRange(slot.startHour, timezone)}
                        value={value}
                        valueNoun="events"
                        {...(shareOfPeak != null ? { detail: shareOfPeak } : {})}
                        className="h-5 rounded"
                        style={{ backgroundColor: heatColor(value, max) }}
                      />
                    );
                  })}
                </div>
              ))}
              <div />
              {DAYS.map((day) => (
                <div key={day.short} className="pt-1 text-center text-[10px] text-muted-foreground">
                  {day.short}
                </div>
              ))}
            </div>
            {note ? <NotReadyNote className="mt-3">{note}</NotReadyNote> : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
